<template>
  <div class="page-container vip-level-page">
    <el-breadcrumb separator="/">
      <el-breadcrumb-item>
        <span class="page-title">VIP管理</span>
      </el-breadcrumb-item>
      <el-breadcrumb-item>VIP等级管理</el-breadcrumb-item>
    </el-breadcrumb>

    <el-card class="page-content" shadow="never">
      <div class="toolbar">
        <div class="table-description">配置VIP0至VIP18等级门槛及审批规则</div>
        <div class="toolbar-actions">
          <el-button
            v-if="canConfigureThreshold"
            size="small"
            icon="el-icon-setting"
            @click="openThresholdConfig"
          >
            门槛配置
          </el-button>
          <el-tooltip
            v-if="canAdd"
            :disabled="!isMaxVipReached"
            content="VIP等级最多只能配置到VIP18"
            placement="top"
          >
            <span>
              <el-button
                type="primary"
                size="small"
                :disabled="isMaxVipReached"
                @click="openCreate"
              >
                新增
              </el-button>
            </span>
          </el-tooltip>
        </div>
      </div>

      <el-table :data="rows" border size="small" style="width: 100%">
        <el-table-column prop="vipLevel" label="VIP等级" width="100" align="center">
          <template slot-scope="scope">VIP{{ scope.row.vipLevel }}</template>
        </el-table-column>
        <el-table-column
          v-for="type in thresholdTypes"
          :key="type.code"
          :label="thresholdColumnLabel(type)"
          min-width="150"
          align="center"
        >
          <template slot-scope="scope">{{ thresholdValue(scope.row, type.code) }}</template>
        </el-table-column>
        <el-table-column label="单次降级限制" width="130" align="center">
          <template slot-scope="scope">{{ downgradeLimitText(scope.row.downgradeLimit) }}</template>
        </el-table-column>
        <el-table-column label="是否需要审批" width="120" align="center">
          <template slot-scope="scope">{{ scope.row.approvalRequired ? '是' : '否' }}</template>
        </el-table-column>
        <el-table-column label="创建时间" width="170">
          <template slot-scope="scope">{{ formatTime(scope.row.createTime) }}</template>
        </el-table-column>
        <el-table-column prop="createBy" label="创建人" width="120" />
        <el-table-column label="更新时间" width="170">
          <template slot-scope="scope">{{ formatTime(scope.row.updateTime) }}</template>
        </el-table-column>
        <el-table-column prop="updateBy" label="更新人" width="120" />
        <el-table-column label="操作" width="180" fixed="right" align="center">
          <template slot-scope="scope">
            <el-button v-if="canDetail" type="text" size="small" @click="openDetail(scope.row)">详情</el-button>
            <el-button v-if="canEdit" type="text" size="small" @click="openEdit(scope.row)">修改</el-button>
            <el-button v-if="canDelete && scope.row.canDelete" type="text" size="small" @click="confirmDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pager"
        background
        layout="total, prev, pager, next, sizes"
        :total="total"
        :page-size="pageSize"
        :current-page.sync="page"
        :page-sizes="[10, 20, 50]"
        @size-change="onSizeChange"
        @current-change="fetchList"
      />
    </el-card>

    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="860px"
      :close-on-click-modal="false"
      @closed="resetForm"
    >
      <el-form ref="form" :model="form" label-width="130px" size="small" :disabled="isDetail">
        <div class="section-title">基础信息</div>
        <el-form-item label="VIP等级" required>
          <el-input :value="`VIP${form.vipLevel || ''}`" disabled />
        </el-form-item>
        <el-form-item label="是否需要审批" required>
          <el-radio-group v-model="form.approvalRequired">
            <el-radio :label="false">否</el-radio>
            <el-radio :label="true">是</el-radio>
          </el-radio-group>
        </el-form-item>

        <div class="section-line"></div>
        <div class="section-title required-title">
          门槛配置
          <span class="tip">至少录入一种已启用门槛</span>
        </div>
        <div
          v-for="type in thresholdTypes"
          :key="type.code"
          class="threshold-card"
        >
          <div class="threshold-row">
            <el-input :value="thresholdTypeDisplayName(type)" disabled class="threshold-name" />
            <el-select v-model="thresholdForm(type.code).compareOperator" disabled class="operator-select">
              <el-option
                v-for="op in compareOperators"
                :key="op.code"
                :label="op.symbol"
                :value="op.code"
              />
            </el-select>
            <el-input
              v-model.trim="thresholdForm(type.code).thresholdValue"
              :disabled="isDetail"
              :placeholder="isDetail ? '--' : '请输入门槛值'"
              maxlength="15"
              class="threshold-value"
              @input="onThresholdValueInput(type.code, $event)"
            />
            <el-input :value="type.unit" disabled class="unit-input" />
          </div>
          <div class="retention-box">
            <div class="retention-title">保级规则</div>
            <div class="retention-row">
              <span class="retention-label">保级周期</span>
              <el-select v-model="thresholdForm(type.code).retentionCycleType" disabled class="cycle-select">
                <el-option
                  v-for="cycle in retentionCycleTypes"
                  :key="cycle.code"
                  :label="cycle.label"
                  :value="cycle.code"
                />
              </el-select>
              <span class="retention-label grace-label">宽限期</span>
              <el-input
                v-model.trim="thresholdForm(type.code).retentionGraceDays"
                :disabled="isDetail || !hasThresholdValue(type.code)"
                :placeholder="isDetail ? '--' : '请输入宽限期'"
                maxlength="3"
                class="grace-input"
                @input="onRetentionGraceDaysInput(type.code, $event)"
              />
              <span class="day-unit">天</span>
            </div>
          </div>
        </div>

        <div class="section-line"></div>
        <div class="section-title">降级规则</div>
        <el-form-item label="单次降级限制" required>
          <el-select v-model="form.downgradeLimit" placeholder="请选择单次降级限制" style="width: 100%">
            <el-option label="不限制" :value="0" />
            <el-option v-for="n in [1, 2, 3, 4, 5]" :key="n" :label="String(n)" :value="n" />
          </el-select>
        </el-form-item>

        <template v-if="!isEdit && !isDetail">
          <div class="section-line"></div>
          <div class="section-title">费率管理</div>
          <div
            v-for="group in rateGroups"
            :key="group.code"
            class="rate-market-group"
          >
            <div class="rate-market-title">{{ group.label }}</div>
            <div class="rate-grid">
              <div
                v-for="(type, index) in group.types"
                :key="type.code"
                class="rate-column"
                :class="{ 'rate-column-divider': index > 0 }"
              >
                <div class="rate-column-title">
                  {{ rateRoleLabel(type) }}手续费费率
                  <span class="required-mark">*</span>
                </div>
                <div class="rate-field-label">
                  手续费折扣
                  <span class="required-mark">*</span>
                </div>
                <el-input
                  v-model.trim="rateForm(type.code).discountPercent"
                  class="rate-input"
                  placeholder="请输入手续费折扣"
                  @input="onRateInput(type.code, $event)"
                >
                  <template slot="append">%</template>
                </el-input>
                <div class="rate-field-label">具体手续费率</div>
                <el-input
                  :value="specificFeeRate(type)"
                  class="rate-input specific-rate-input"
                  disabled
                />
              </div>
            </div>
          </div>
          <div class="rate-explanation">
            <i class="el-icon-info rate-explanation-icon"></i>
            <span>{{ rateExplanation }}</span>
          </div>
        </template>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">{{ isDetail ? '关 闭' : '取 消' }}</el-button>
        <el-button v-if="!isDetail" type="primary" @click="confirmSubmit">
          {{ isEdit ? '确认修改' : '确认新增' }}
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="门槛配置"
      :visible.sync="thresholdConfigVisible"
      width="520px"
      :close-on-click-modal="false"
    >
      <div class="threshold-config-list">
        <div
          v-for="item in thresholdConfigItems"
          :key="item.thresholdType"
          class="threshold-config-row"
        >
          <div>
            <div class="threshold-config-name">{{ item.thresholdName }}</div>
            <div class="threshold-config-unit">单位：{{ item.unit || '--' }}</div>
          </div>
          <div class="threshold-config-switch">
            <span>{{ item.enabled ? '启用' : '停用' }}</span>
            <el-switch v-model="item.enabled" />
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="thresholdConfigVisible = false">取 消</el-button>
        <el-button type="primary" :loading="thresholdConfigSaving" @click="saveThresholdConfig">
          确认保存
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { Message, MessageBox } from 'element-ui';
import BigNumber from 'bignumber.js';
import { hasPerm } from 'common/permission';
import api from './api';

function emptyForm() {
  return {
    id: null,
    vipLevel: null,
    approvalRequired: false,
    downgradeLimit: 0,
    thresholds: {},
    rates: {},
  };
}

export default {
  name: 'VipLevelPage',
  data() {
    return {
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      thresholdTypes: [],
      retentionCycleTypes: [],
      compareOperators: [],
      rateTypes: [],
      dialogVisible: false,
      isEdit: false,
      isDetail: false,
      form: emptyForm(),
      maxVipLevel: 18,
      thresholdConfigVisible: false,
      thresholdConfigSaving: false,
      thresholdConfigLoaded: false,
      thresholdConfigItems: [],
      enabledThresholdTypes: [],
    };
  },
  computed: {
    ...mapState(['userInfo']),
    canAdd() {
      return hasPerm(this.userInfo || {}, 'tenant:vip_level:add');
    },
    canEdit() {
      return hasPerm(this.userInfo || {}, 'tenant:vip_level:edit');
    },
    canDetail() {
      return hasPerm(this.userInfo || {}, 'tenant:vip_level:detail');
    },
    canDelete() {
      return hasPerm(this.userInfo || {}, 'tenant:vip_level:delete');
    },
    canConfigureThreshold() {
      return hasPerm(this.userInfo || {}, 'tenant:vip_level:threshold_config');
    },
    isMaxVipReached() {
      return this.total >= this.maxVipLevel || this.rows.some((row) => Number(row.vipLevel) >= this.maxVipLevel);
    },
    dialogTitle() {
      if (this.isDetail) return 'VIP等级详情';
      return this.isEdit ? '修改VIP等级' : '新增VIP等级';
    },
    defaultCompareOperator() {
      return (this.compareOperators[0] && this.compareOperators[0].code) || 'GTE';
    },
    defaultRetentionCycleType() {
      return (this.retentionCycleTypes[0] && this.retentionCycleTypes[0].code) || 'DAILY';
    },
    rateGroups() {
      return [
        { code: 'SPOT', label: '现货手续费' },
        { code: 'CONTRACT', label: '合约手续费' },
      ].map((group) => ({
        ...group,
        types: this.rateTypes.filter((type) => String(type.code || '').startsWith(`${group.code}_`)),
      })).filter((group) => group.types.length);
    },
    rateExplanation() {
      const baseRates = this.rateTypes
        .filter((type) => type.baseRate !== null && type.baseRate !== undefined)
        .map((type) => `${type.label} ${this.baseFeeRate(type)}`)
        .join('、');
      const baseRateText = baseRates ? `，当前基础费率为${baseRates}` : '';
      return `说明：录入手续费折扣后，系统将按基础费率自动计算具体手续费率${baseRateText}；具体手续费率不支持修改。`;
    },
  },
  created() {
    this.loadEnums();
    this.loadThresholdConfig();
    this.fetchList();
  },
  methods: {
    async loadEnums() {
      const res = await api.enums();
      const data = (res && res.data) || {};
      this.applyEnums(data);
    },
    applyEnums(data) {
      this.thresholdTypes = data.thresholdTypes || [];
      this.retentionCycleTypes = data.retentionCycleTypes || [];
      this.compareOperators = data.compareOperators || [];
      this.rateTypes = data.rateTypes || [];
    },
    async loadThresholdConfig() {
      const res = await api.thresholdConfig();
      const items = (res && res.data) || [];
      this.thresholdConfigItems = items.map((item) => ({ ...item, enabled: !!item.enabled }));
      this.enabledThresholdTypes = this.thresholdConfigItems
        .filter((item) => item.enabled)
        .map((item) => item.thresholdType);
      this.thresholdConfigLoaded = true;
    },
    async fetchList() {
      const res = await api.page({ page: this.page, size: this.pageSize });
      const data = (res && res.data) || {};
      this.rows = data.items || [];
      this.total = data.total || 0;
    },
    onSizeChange(size) {
      this.pageSize = size;
      this.page = 1;
      this.fetchList();
    },
    thresholdColumnLabel(type) {
      return `${type.label}${type.unit ? `(${type.unit})` : ''}`;
    },
    thresholdTypeDisplayName(type) {
      const enabled = this.enabledThresholdTypes.includes(type.code);
      return `${type.label}（${enabled ? '启用' : '停用'}）`;
    },
    thresholdValue(row, typeCode) {
      const item = (row.thresholds || []).find((t) => t.thresholdType === typeCode);
      return item && item.thresholdValue != null ? item.thresholdValue : '--';
    },
    downgradeLimitText(value) {
      const n = Number(value || 0);
      return n === 0 ? '不限制' : `${n}级`;
    },
    formatTime(value) {
      if (!value) return '--';
      let d;
      if (Array.isArray(value)) {
        d = new Date(value[0], value[1] - 1, value[2], value[3] || 0, value[4] || 0, value[5] || 0);
      } else {
        d = new Date(value);
      }
      if (Number.isNaN(d.getTime())) return '--';
      const p = (x) => String(x).padStart(2, '0');
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
    },
    ensureThreshold(typeCode) {
      if (!this.form.thresholds[typeCode]) {
        this.$set(this.form.thresholds, typeCode, {
          thresholdType: typeCode,
          compareOperator: this.defaultCompareOperator,
          thresholdValue: '',
          retentionCycleType: this.defaultRetentionCycleType,
          retentionGraceDays: '',
        });
      }
      return this.form.thresholds[typeCode];
    },
    thresholdForm(typeCode) {
      const item = this.ensureThreshold(typeCode);
      if (this.isDetail) {
        return {
          ...item,
          thresholdValue: item.thresholdValue === '' || item.thresholdValue == null ? '--' : item.thresholdValue,
          retentionGraceDays: item.retentionGraceDays === '' || item.retentionGraceDays == null ? '--' : item.retentionGraceDays,
        };
      }
      return item;
    },
    ensureRate(typeCode) {
      if (!this.form.rates[typeCode]) {
        this.$set(this.form.rates, typeCode, {
          rateType: typeCode,
          discountPercent: '100',
        });
      }
      return this.form.rates[typeCode];
    },
    rateForm(typeCode) {
      return this.ensureRate(typeCode);
    },
    resetThresholdsFromList(list = [], detailMode = false) {
      const map = {};
      this.thresholdTypes.forEach((type) => {
        const item = list.find((t) => t.thresholdType === type.code);
        map[type.code] = {
          thresholdType: type.code,
          compareOperator: (item && item.compareOperator) || this.defaultCompareOperator,
          thresholdValue: item && item.thresholdValue != null ? String(item.thresholdValue) : '',
          retentionCycleType: (item && item.retentionCycleType) || this.defaultRetentionCycleType,
          retentionGraceDays: item && item.retentionGraceDays != null ? String(item.retentionGraceDays) : '',
        };
        if (detailMode && !item) {
          map[type.code].thresholdValue = '';
          map[type.code].retentionGraceDays = '';
        }
      });
      this.form.thresholds = map;
    },
    resetRatesFromList(list = []) {
      const map = {};
      this.rateTypes.forEach((type) => {
        const item = list.find((t) => t.rateType === type.code);
        map[type.code] = {
          rateType: type.code,
          discountPercent: item && item.discountRate != null
            ? this.toPercentInput(item.discountRate)
            : '100',
        };
      });
      this.form.rates = map;
    },
    toPercentInput(value) {
      return new BigNumber(value).times(100).toFixed();
    },
    async openCreate() {
      if (!this.thresholdConfigLoaded) await this.loadThresholdConfig();
      this.isEdit = false;
      this.isDetail = false;
      const res = await api.next();
      const data = (res && res.data) || {};
      this.maxVipLevel = data.maxVipLevel || this.maxVipLevel;
      if (data.canCreate === false) {
        Message.warning(`VIP等级最多只能配置到VIP${this.maxVipLevel}`);
        return;
      }
      if (data.enums) {
        this.applyEnums(data.enums);
      }
      this.form = emptyForm();
      this.form.vipLevel = data.vipLevel;
      this.form.approvalRequired = false;
      this.form.downgradeLimit = 0;
      this.resetThresholdsFromList([]);
      this.resetRatesFromList(data.previousRates || []);
      this.dialogVisible = true;
    },
    async openEdit(row) {
      if (!this.thresholdConfigLoaded) await this.loadThresholdConfig();
      this.isEdit = true;
      this.isDetail = false;
      const res = await api.detail(row.id);
      this.fillForm((res && res.data) || {});
      this.dialogVisible = true;
    },
    async openDetail(row) {
      if (!this.thresholdConfigLoaded) await this.loadThresholdConfig();
      this.isEdit = false;
      this.isDetail = true;
      const res = await api.detail(row.id);
      this.fillForm((res && res.data) || {}, true);
      this.dialogVisible = true;
    },
    fillForm(data, detailMode = false) {
      this.form = emptyForm();
      this.form.id = data.id;
      this.form.vipLevel = data.vipLevel;
      this.form.approvalRequired = !!data.approvalRequired;
      this.form.downgradeLimit = data.downgradeLimit == null ? 0 : data.downgradeLimit;
      this.resetThresholdsFromList(data.thresholds || [], detailMode);
    },
    resetForm() {
      this.isEdit = false;
      this.isDetail = false;
      this.form = emptyForm();
    },
    buildThresholdPayloads() {
      return this.thresholdTypes
        .map((type) => this.form.thresholds[type.code])
        .filter((item) => item && item.thresholdValue !== '' && item.thresholdValue !== null && item.thresholdValue !== undefined)
        .map((item) => ({
          thresholdType: item.thresholdType,
          compareOperator: item.compareOperator || this.defaultCompareOperator,
          thresholdValue: item.thresholdValue,
          retentionCycleType: item.retentionCycleType || this.defaultRetentionCycleType,
          retentionGraceDays: item.retentionGraceDays,
        }));
    },
    hasThresholdValue(typeCode) {
      const item = this.form.thresholds[typeCode];
      return !!item && item.thresholdValue !== '' && item.thresholdValue !== null && item.thresholdValue !== undefined;
    },
    onThresholdValueInput(typeCode, value) {
      const item = this.ensureThreshold(typeCode);
      item.thresholdValue = this.sanitizeIntegerInput(value, 15);
      if (item.thresholdValue === '') item.retentionGraceDays = '';
    },
    onRetentionGraceDaysInput(typeCode, value) {
      this.ensureThreshold(typeCode).retentionGraceDays = this.sanitizeIntegerInput(value, 3);
    },
    onRateInput(typeCode, value) {
      this.ensureRate(typeCode).discountPercent = this.sanitizeDecimalInput(value, 2);
    },
    rateRoleLabel(type) {
      const code = String((type && type.code) || '');
      if (code.endsWith('_MAKER')) return 'Maker';
      if (code.endsWith('_TAKER')) return 'Taker';
      return (type && type.label) || '';
    },
    baseFeeRate(type) {
      if (!type || type.baseRate === null || type.baseRate === undefined) return '--';
      const baseRate = new BigNumber(type.baseRate);
      if (!baseRate.isFinite()) return '--';
      return `${this.trimRateNumber(baseRate.times(100))}%`;
    },
    specificFeeRate(type) {
      const rate = type && this.form.rates[type.code];
      if (!rate || rate.discountPercent === '' || type.baseRate === null || type.baseRate === undefined) {
        return '--';
      }
      const discountPercent = new BigNumber(rate.discountPercent);
      const baseRate = new BigNumber(type.baseRate);
      if (!discountPercent.isFinite() || !baseRate.isFinite() || discountPercent.lte(0)) return '--';
      const actualPercent = baseRate.times(discountPercent.div(100)).times(100);
      return `${this.trimRateNumber(actualPercent)}%`;
    },
    trimRateNumber(value) {
      return new BigNumber(value).decimalPlaces(8, BigNumber.ROUND_DOWN).toFixed();
    },
    sanitizeIntegerInput(value, maxDigits) {
      return String(value == null ? '' : value).replace(/\D/g, '').slice(0, maxDigits);
    },
    sanitizeDecimalInput(value, maxDecimalPlaces) {
      const text = String(value == null ? '' : value).replace(/[^\d.]/g, '');
      const dotIndex = text.indexOf('.');
      if (dotIndex < 0) return text;
      const integerPart = text.slice(0, dotIndex) || '0';
      const decimalPart = text.slice(dotIndex + 1).replace(/\./g, '').slice(0, maxDecimalPlaces);
      return `${integerPart}.${decimalPart}`;
    },
    buildRatePayloads() {
      if (this.isEdit || this.isDetail) return [];
      return this.rateTypes.map((type) => {
        const item = this.form.rates[type.code] || {};
        return {
          rateType: type.code,
          discountRate: new BigNumber(item.discountPercent || 0).div(100).toFixed(),
        };
      });
    },
    validateForm() {
      const payloads = this.buildThresholdPayloads();
      if (!payloads.some((item) => this.enabledThresholdTypes.includes(item.thresholdType))) {
        Message.warning('至少录入一种已启用门槛');
        return false;
      }
      for (const item of payloads) {
        const thresholdValue = String(item.thresholdValue);
        if (!/^\d{1,15}$/.test(thresholdValue) || new BigNumber(thresholdValue).lte(0)) {
          Message.warning('门槛值必须是大于0且最多15位的整数');
          return false;
        }
        if (!/^\d{1,3}$/.test(String(item.retentionGraceDays == null ? '' : item.retentionGraceDays))) {
          Message.warning('保级宽限期必须是0到999的整数');
          return false;
        }
      }
      if (!this.isEdit) {
        for (const type of this.rateTypes) {
          const rate = this.form.rates[type.code];
          if (!rate || rate.discountPercent === '') {
            Message.warning('请填写完整的VIP费率折扣');
            return false;
          }
          const discountPercent = String(rate.discountPercent);
          if (!/^\d+(?:\.\d{1,2})?$/.test(discountPercent)
              || new BigNumber(discountPercent).lte(0)) {
            Message.warning('费率折扣必须大于0且最多支持2位小数');
            return false;
          }
        }
      }
      return true;
    },
    confirmSubmit() {
      if (this.isDetail || !this.validateForm()) return;
      MessageBox.confirm(`确认${this.isEdit ? '修改' : '新增'}该VIP等级？`, '提示', { type: 'warning' })
        .then(() => this.doSubmit())
        .catch(() => {});
    },
    async doSubmit() {
      const body = {
        id: this.form.id,
        vipLevel: this.form.vipLevel,
        approvalRequired: this.form.approvalRequired,
        downgradeLimit: this.form.downgradeLimit,
        thresholds: this.buildThresholdPayloads(),
        rates: this.buildRatePayloads(),
      };
      if (this.isEdit) {
        await api.update(body);
      } else {
        await api.save(body);
      }
      Message.success('保存成功');
      this.dialogVisible = false;
      this.fetchList();
    },
    async openThresholdConfig() {
      await this.loadThresholdConfig();
      this.thresholdConfigVisible = true;
    },
    async saveThresholdConfig() {
      const enabledThresholdTypes = this.thresholdConfigItems
        .filter((item) => item.enabled)
        .map((item) => item.thresholdType);
      if (!enabledThresholdTypes.length) {
        Message.warning('至少启用一种VIP门槛');
        return;
      }
      this.thresholdConfigSaving = true;
      try {
        await api.saveThresholdConfig({ enabledThresholdTypes });
        this.enabledThresholdTypes = enabledThresholdTypes;
        this.thresholdConfigVisible = false;
        Message.success('保存成功');
      } finally {
        this.thresholdConfigSaving = false;
      }
    },
    confirmDelete(row) {
      MessageBox.confirm(`确认删除VIP${row.vipLevel}？删除后会同步删除该等级门槛、费率和费率历史。`, '提示', { type: 'warning' })
        .then(async () => {
          await api.delete(row.id);
          Message.success('删除成功');
          this.fetchList();
        })
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
.page-content {
  margin-top: 12px;
}
.toolbar {
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.table-description {
  color: #111c3d;
  font-size: 14px;
  font-weight: 500;
}
.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.pager {
  margin-top: 16px;
  text-align: right;
}
.section-title {
  margin: 8px 0 18px;
  font-weight: 600;
  color: #111c3d;
}
.required-title::after {
  content: " *";
  color: #f56c6c;
}
.tip {
  margin-left: 10px;
  color: #ff7a00;
  font-weight: 400;
}
.section-line {
  border-top: 1px solid #e5eaf5;
  margin: 18px 0 22px;
}
.threshold-card {
  border: 1px solid #dfe7f3;
  border-radius: 6px;
  padding: 14px 16px;
  margin-bottom: 12px;
}
.threshold-row,
.retention-row {
  display: flex;
  align-items: center;
  gap: 14px;
}
.threshold-name {
  width: 280px;
}
.operator-select {
  width: 88px;
}
.threshold-value {
  flex: 1;
}
.unit-input {
  width: 100px;
}
.retention-box {
  border: 1px solid #dfe7f3;
  border-radius: 6px;
  margin-top: 14px;
  padding: 14px;
}
.retention-title {
  margin-bottom: 12px;
  color: #111c3d;
  font-weight: 600;
}
.retention-label {
  color: #111c3d;
  white-space: nowrap;
}
.cycle-select {
  width: 240px;
}
.grace-label {
  margin-left: 24px;
}
.grace-input {
  width: 200px;
}
.day-unit {
  color: #111c3d;
}
.rate-market-group {
  padding-bottom: 18px;
}
.rate-market-group + .rate-market-group {
  padding-top: 18px;
  border-top: 1px solid #e5eaf5;
}
.rate-market-title {
  margin-bottom: 14px;
  color: #111c3d;
  font-size: 14px;
  font-weight: 600;
}
.rate-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.rate-column {
  min-width: 0;
  padding-right: 30px;
}
.rate-column-divider {
  padding-right: 0;
  padding-left: 30px;
  border-left: 1px solid #dfe7f3;
}
.rate-column-title {
  margin-bottom: 12px;
  color: #111c3d;
  font-weight: 500;
}
.required-mark {
  color: #f56c6c;
}
.rate-field-label {
  margin-bottom: 7px;
  color: #5f6b85;
  font-size: 13px;
}
.rate-input {
  width: 100%;
  max-width: 260px;
  margin-bottom: 12px;
}
.specific-rate-input {
  margin-bottom: 0;
}
.threshold-config-list {
  border-top: 1px solid #e5eaf5;
}
.threshold-config-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 68px;
  padding: 0 4px;
  border-bottom: 1px solid #e5eaf5;
}
.threshold-config-name {
  color: #111c3d;
  font-weight: 500;
}
.threshold-config-unit {
  margin-top: 5px;
  color: #8a94a8;
  font-size: 12px;
}
.threshold-config-switch {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #5f6b85;
}
.rate-explanation {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #b9d5ff;
  border-radius: 4px;
  background: #f4f8ff;
  color: #3f5f8f;
  font-size: 13px;
  line-height: 20px;
}
.rate-explanation-icon {
  margin-top: 3px;
  color: #409eff;
  font-size: 15px;
}
</style>
